import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useJobRoleNames, useUpdateContributorNameAndJobRole } from './api';
import { useQueryClient } from '@tanstack/react-query';
import { useToastHook } from '../appToast';
import { useFormik } from 'formik';
import { validationSchema } from './validationSchema';
import { useEffect, useState } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';

interface EditContributorModalProps {
  isOpen: boolean;
  onClose: () => void;
  contributorEmail?: string;
  contributorFirstName?: string;
  contributorLastName?: string;
  contributorRole?: string;
}

export const EditContributorModal = ({
  isOpen,
  onClose,
  contributorEmail,
  contributorFirstName,
  contributorLastName,
  contributorRole,
}: EditContributorModalProps) => {
  const {
    mutate: updateContributorJobRole,
    isLoading: updateContributorJobRoleIsInProgress,
  } = useUpdateContributorNameAndJobRole();
  const { data, isLoading } = useJobRoleNames();

  const queryClient = useQueryClient();
  const [newToast] = useToastHook();

  const [initialValues, setInitialValues] = useState({
    firstName: '',
    lastName: '',
    role: '',
  });

  useEffect(() => {
    setInitialValues({
      firstName: contributorFirstName || '',
      lastName: contributorLastName || '',
      role: contributorRole || '',
    });
  }, [contributorFirstName, contributorLastName, contributorRole, isOpen]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateContributorJobRole(
        {
          email: contributorEmail,
          firstName: values.firstName,
          lastName: values.lastName,
          jobRole: values.role,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['contributorDetails']);
            queryClient.invalidateQueries(['contributorJobRole']);
            queryClient.invalidateQueries(['contributorList']);
            newToast({
              message: 'Contributor details updated successfully',
              status: 'success',
            });
            setTimeout(() => {
              onClose();
            }, 200);
          },
          onError: () => {
            newToast({
              message: 'Error updating contributor details',
              status: 'error',
            });
          },
        }
      );
    },
  });

  const jobRoleOptions = data?.dto || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      motionPreset="slideInBottom"
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        borderRadius={'4px'}
        boxShadow="0px 0px 14px rgba(0, 0, 0, 0.1)"
        fontFamily={'inter'}
      >
        <ModalCloseButton onClick={onClose} />
        <form onSubmit={formik.handleSubmit}>
          <ModalBody p={4} mt={3}>
            <Flex mb={4}>
              <FormControl mr={2} isInvalid={!!formik.errors.firstName}>
                <FormLabel color="text.secondary2" fontWeight="500">
                  First Name
                </FormLabel>
                <Input
                  placeholder={contributorFirstName}
                  id="firstName"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.errors.firstName && (
                  <FormErrorMessage fontSize={'12px'}>
                    {formik.errors.firstName}
                  </FormErrorMessage>
                )}
              </FormControl>
              <FormControl ml={2} isInvalid={!!formik.errors.lastName}>
                <FormLabel color="text.secondary2" fontWeight="500">
                  Last Name
                </FormLabel>
                <Input
                  placeholder={contributorLastName}
                  id="lastName"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.errors.lastName && (
                  <FormErrorMessage fontSize={'12px'}>
                    {formik.errors.lastName}
                  </FormErrorMessage>
                )}
              </FormControl>
            </Flex>
            <FormControl isInvalid={!!formik.errors.role}>
              <FormLabel color="text.secondary2" fontWeight="500">
                Job Role
              </FormLabel>
              <Menu matchWidth={true}>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  variant="outline"
                  width="100%"
                  textAlign="left"
                  borderColor="primary"
                  isDisabled={isLoading}
                  bg="white"
                  _hover={{ bg: 'gray.50' }}
                  _active={{ bg: 'gray.100' }}
                  height="40px"
                  justifyContent="space-between"
                >
                  <Text
                    isTruncated
                    color={formik.values.role ? 'black' : 'gray.500'}
                    fontWeight={'normal'}
                    fontSize={'14px'}
                  >
                    {formik.values.role ||
                      (isLoading
                        ? 'Loading job roles...'
                        : 'Select a job role')}
                  </Text>
                </MenuButton>

                <MenuList
                  maxH="200px"
                  overflowY="auto"
                  zIndex={1600}
                  width="100%"
                  minW="100%"
                >
                  {isLoading ? (
                    <MenuItem isDisabled>
                      <Spinner size="sm" mr={2} />
                      Loading job roles...
                    </MenuItem>
                  ) : jobRoleOptions.length === 0 ? (
                    <MenuItem isDisabled>No job roles available</MenuItem>
                  ) : (
                    jobRoleOptions.map((roleName: string, index: number) => (
                      <MenuItem
                        key={`${roleName}-${index}`}
                        onClick={() => {
                          formik.setFieldValue('role', roleName);
                          formik.setFieldTouched('role', true);
                        }}
                        _hover={{ bg: 'primary', color: 'white' }}
                        _focus={{ bg: 'primary', color: 'white' }}
                      >
                        {roleName}
                      </MenuItem>
                    ))
                  )}
                </MenuList>
              </Menu>
              {formik.errors.role && (
                <FormErrorMessage fontSize={'12px'}>
                  {formik.errors.role}
                </FormErrorMessage>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              color={'text.secondary2'}
              p={'12px 16px'}
              bg={'transparent'}
              onClick={onClose}
              fontSize={'16px'}
              fontWeight={500}
              _hover={{ bg: 'white' }}
            >
              Cancel
            </Button>
            <Button
              ml={'8px'}
              color={'white'}
              p={'12px 16px'}
              bg={'primary'}
              border={'1px solid #E0E0E0'}
              borderRadius={'4px'}
              fontSize={'16px'}
              fontWeight={500}
              _hover={{ bg: 'primary' }}
              w={20}
              type="submit"
              isDisabled={
                !formik.isValid || updateContributorJobRoleIsInProgress
              }
            >
              {updateContributorJobRoleIsInProgress ? (
                <Spinner size="sm" />
              ) : (
                'Save'
              )}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default EditContributorModal;
